import { useCallback, useState } from 'react'
import * as React from 'react'
import Dialog from '../../Common/Dialog/Dialog'
import { type UserProductDesignStore } from '../../UserData/_stores/UserProductDesignStore'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from '../../Common/_hooks/useAppDispatch'
import { IconButton, InputAdornment, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, TextField } from '@mui/material'
import { AddRounded as AddRoundedIcon, CloseRounded as CloseRoundedIcon, CheckRounded as CheckRoundedIcon } from '@mui/icons-material'
import DialogTitle from '../../Common/Dialog/DialogTitle'
import { BatchAddToCollection, CreateCollection } from '../../UserData/_actions/CollectionActions'
import { useAppSelector } from '../../Common/_hooks/useAppSelector'
import { type CollectionStore } from '../../UserData/_stores/CollectionStore'
import { type BatchProductDesignSelection } from '../../UserData/_actions/ProductDesignActions'
import { ClearProductDesignSelectedProduct } from '../../UIData/_actions/Account/AccountStoreActions'

type Props = {
  opened: boolean
  onClose: () => void
} & ({
  productDesign: UserProductDesignStore
  selection?: never
  selectionCount?: never
} | {
  productDesign?: never
  selection: BatchProductDesignSelection
  selectionCount: number
})

export default function AddToCollectionDialog(props: Props) {
  const [t] = useTranslation('account-v2')
  const dispatch = useAppDispatch()

  const collections = useAppSelector(state => state.get('userData').get('collections'))
  const loader = useAppSelector(state => state.get('UIData').get('loaders').get('edit_product_designs'))

  const [newCollectionOpened, setNewCollectionOpened] = useState(false)
  const [name, setName] = useState('')
  const isBatch = !!props.selection

  const resetDialogState = useCallback(() => {
    setNewCollectionOpened(false)
    setName('')
  }, [])

  const onClose = useCallback(() => {
    props.onClose()
  }, [props.onClose])

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value)
  }, [])

  const addToCollection = useCallback((collectionId: number) => {
    const selection = props.selection ?? {
      ids: [props.productDesign.get('id')],
    }

    dispatch(BatchAddToCollection(collectionId, selection).set({
      onSuccess: () => {
        if (props.selection) {
          dispatch(ClearProductDesignSelectedProduct())
          onClose()
        }
      }
    }))
  }, [dispatch, onClose, props])

  const onSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    dispatch(CreateCollection(name).set({
      onSuccess: (response) => {
        setNewCollectionOpened(false)
        setName('')
        addToCollection(response.data.item.id)
      }
    }))
  }, [addToCollection, dispatch, name])

  const onNewCollectionOpen = useCallback(() => {
    setNewCollectionOpened(true)
  }, [])

  const onNewCollectionClose = useCallback(() => {
    setNewCollectionOpened(false)
  }, [])

  const renderCollection = useCallback((collection: CollectionStore) => {
    // Disable this collection if product design is already part of it
    let isInCollection = false
    if (props.productDesign) {
      collection.get('listItems').forEach(item => {
        if (item.get('id_product_design') === props.productDesign?.get('id_product_design')) {
          isInCollection = true
          return false
        }
      })
    }

    return <ListItemButton
      key={collection.get('id')}
      disabled={isInCollection || (isBatch && !!loader)}
      onClick={() => {
        addToCollection(collection.get('id'))
      }}
    >
      <ListItemText>{collection.get('name')}</ListItemText>
      { isInCollection ? <CheckRoundedIcon/> : null }
    </ListItemButton>
  }, [addToCollection, isBatch, loader, props.productDesign])

  return <>
    <Dialog
      open={props.opened}
      maxWidth="xs"
      fullWidth
      onClose={onClose}
      TransitionProps={{
        onExited: resetDialogState,
      }}
      showCloseButton
    >
      <DialogTitle>{props.selection
        ? t('Add {{count}} products to collection', { count: props.selectionCount })
        : t('Add to collection')}</DialogTitle>
      <List>
        {collections.valueSeq().map(renderCollection)}

        {!newCollectionOpened ? <ListItemButton
          disabled={isBatch && !!loader}
          onClick={onNewCollectionOpen}
        >
          <ListItemIcon><AddRoundedIcon color="primary" /></ListItemIcon>
          <ListItemText sx={{ color: 'primary.main' }}>{t('New collection')}</ListItemText>
        </ListItemButton> : null}

        {newCollectionOpened ? <ListItem>
          <form onSubmit={onSubmit}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                autoFocus
                variant="filled"
                error={name === ''}
                value={name}
                onChange={onChange}
                label={t('Name')}
                InputProps={{
                  endAdornment: <InputAdornment
                    position="end"
                  >
                    <IconButton
                      onClick={onNewCollectionClose}
                      edge="end"
                      disabled={isBatch && !!loader}
                    ><CloseRoundedIcon /></IconButton>
                  </InputAdornment>
                }}
              />
              <IconButton
                color="primary"
                size="large"
                type="submit"
                disabled={name === '' || (isBatch && !!loader)}
              ><CheckRoundedIcon /></IconButton>
            </Stack>
          </form>
        </ListItem> : null}
      </List>
    </Dialog>
  </>
}
